import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Marker, Polyline, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import { siftApiClient } from '../../api/siftApiClient';
import type {
  BrokerVehiclePoint, BrokerSliceMapVehicle, BrokerSliceMapBuyer,
  BrokerChipFilterParams, ClientViewParams,
} from '../../api/types';
import {
  PRIORITY_ORDER, PRIORITY_LABEL, PRIORITY_COLOR,
  customBucketLabel,
  type PriorityKey,
  priorityKeyFromIndex,
  MATCH_TIER_LABEL,
} from './brokerViewTokens';
import type { OutFeature, LeafFeature } from './clusterWorker';
import { spiderfyPositions } from '../common/spiderfyLayout';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue with bundlers
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Markers in the same ZIP resolve to one shared centroid, so their points would
// stack exactly on top of each other. ~66m minimum spacing keeps co-located
// pins individually visible/clickable; the ring radius grows with the group
// size so even large same-ZIP groups don't re-overlap.
const MIN_SEP_DEG = 0.0006;

// Above this a cluster zooms to its own split point instead of fanning out, so
// clicking walks it down in steps. The exception is a cluster sitting entirely
// on one ZIP centroid, which no zoom can separate — that always fans out.
const SPIDERFY_MAX = 50;

/**
 * Fan co-located marker leaves out onto a small ring around their shared
 * centroid. Only individual (non-cluster) markers are passed here, so this runs
 * over at most a few hundred features per viewport. Keyed by the unique marker
 * `id` (multiple markers can share both a seller and a ZIP).
 */
const fanOutLeaves = (
  leaves: { id: string; lat: number; lng: number }[],
): Map<string, [number, number]> => {
  const groups = new Map<string, { id: string; lat: number; lng: number }[]>();
  for (const l of leaves) {
    const key = `${l.lat.toFixed(5)},${l.lng.toFixed(5)}`;
    const g = groups.get(key);
    if (g) g.push(l);
    else groups.set(key, [l]);
  }
  const plot = new Map<string, [number, number]>();
  for (const g of groups.values()) {
    if (g.length === 1) {
      plot.set(g[0].id, [g[0].lat, g[0].lng]);
      continue;
    }
    const ringR = MIN_SEP_DEG / (2 * Math.sin(Math.PI / g.length));
    g.forEach((l, i) => {
      const angle = (2 * Math.PI * i) / g.length;
      const offLat = l.lat + ringR * Math.sin(angle);
      // Keep the ring circular by scaling the east-west offset by cos(lat).
      const offLng = l.lng + (ringR * Math.cos(angle)) / Math.cos((l.lat * Math.PI) / 180);
      plot.set(l.id, [offLat, offLng]);
    });
  }
  return plot;
};

/** Bucket index → group chip label, for the marker popup. */
const daysLabel = (b: number): string => customBucketLabel('days_listed', b) || '—';
const listingLabel = (b: number): string => customBucketLabel('listing_occ', b) || '—';
const bidsLabel = (b: number): string => customBucketLabel('total_bids', b) || '—';

/**
 * Build popup HTML for an aggregated vehicle marker: the owning dealer, the
 * number of vehicles collapsed into the marker, the priority bucket, and the
 * three filter-group buckets (days listed / listing occurrences / total bids).
 */
const buildPopupHtml = (p: BrokerVehiclePoint): string => {
  const priKey = priorityKeyFromIndex(p.priority_bucket);
  const tags = [daysLabel(p.days_bucket), listingLabel(p.listing_bucket), bidsLabel(p.bids_bucket)]
    .map((t) => `<span class="bv-popup-tag">${t}</span>`)
    .join('');
  return `
    <div class="bv-popup">
      <div class="bv-popup-header">
        <div class="bv-popup-name">${p.seller_name || `Dealer ${p.seller_id}`}</div>
        <span class="bv-popup-tier" style="background:${PRIORITY_COLOR[priKey].pin}">${p.count} veh</span>
      </div>
      <div class="bv-popup-id">${p.seller_id}</div>
      <hr class="bv-popup-divider"/>
      <div class="bv-popup-priorities">
        <div><span class="bv-popup-swatch" style="background:${PRIORITY_COLOR[priKey].pin}"></span>${PRIORITY_LABEL[priKey]}</div>
        <div class="bv-popup-tags">${tags}</div>
      </div>
    </div>
  `;
};

interface BrokerViewMapProps {
  points: BrokerVehiclePoint[];
  loading: boolean;
  selectedDealerId?: string | null;
  /** Fired when the user clicks a vehicle marker; pass null to unfocus (background click). */
  onDealerClick?: (sellerId: string | null) => void;
  /** §10-E focus mode: when set, the map drops the clustered board layer and shows
   *  this card's (seller | buyer | cluster) targeted units + candidate buyers + edges. */
  focus?: { kind: 'seller' | 'buyer' | 'cluster'; key: string; name: string } | null;
  /** Board filters forwarded to the slice-map fetch so the focus matches the board. */
  sellerMapParams?: ClientViewParams & BrokerChipFilterParams & { match_types?: string };
  /** Open a focused unit's VIN detail (map-first drill). */
  onFocusVinClick?: (vin: string) => void;
  /** Exit focus mode (back to the board layer). */
  onClearFocus?: () => void;
}

const MapBoundsUpdater: React.FC<{
  points: BrokerVehiclePoint[];
}> = ({ points }) => {
  const map = useMap();
  const prevBoundsKey = useRef('');

  useEffect(() => {
    const boundsKey = `${points.length}-${points[0]?.seller_id || ''}`;
    if (boundsKey === prevBoundsKey.current) return;
    prevBoundsKey.current = boundsKey;

    const coords = points
      .filter((d) => d.latitude != null && d.longitude != null)
      .map((d) => [d.latitude, d.longitude] as [number, number]);
    if (coords.length > 0) {
      const bounds = L.latLngBounds(coords);
      map.fitBounds(bounds, { padding: [40, 40] });
    } else {
      map.setView([39.8283, -98.5795], 4);
    }
  }, [points, map]);

  return null;
};

/**
 * Renders the dealer layer using supercluster (in a Web Worker) for clustering
 * and a shared Leaflet canvas renderer for the individual points.
 *
 * - The thin points are handed to the worker once per data change.
 * - On every pan/zoom we ask the worker for the clusters + leaves inside the
 *   current viewport (a few hundred features at most) and rebuild the layer.
 * - Clusters are DivIcon markers tinted by their hottest vehicle and labelled
 *   with the total vehicle count; individual vehicle markers are L.circleMarker
 *   on one L.canvas() renderer (fill = priority bucket) so thousands draw
 *   without DOM overhead.
 */
const SuperclusterCanvasLayer: React.FC<{
  points: BrokerVehiclePoint[];
  selectedDealerId?: string | null;
  onDealerClick?: (sellerId: string | null) => void;
}> = ({ points, selectedDealerId, onDealerClick }) => {
  const map = useMap();
  const workerRef = useRef<Worker | null>(null);
  const rendererRef = useRef<L.Canvas | null>(null);
  const clusterLayerRef = useRef<L.LayerGroup | null>(null);
  const pointLayerRef = useRef<L.LayerGroup | null>(null);
  const spiderLayerRef = useRef<L.LayerGroup | null>(null);
  const spiderClusterRef = useRef<number | null>(null);
  const circleByIdRef = useRef<Map<string, L.CircleMarker>>(new Map());
  // Marker id (= load index) -> the aggregated vehicle point it represents.
  const pointByIdRef = useRef<Map<string, BrokerVehiclePoint>>(new Map());
  // Seller id -> first marker's [lat, lng], for selection pan/zoom.
  const sellerCenterRef = useRef<Map<string, [number, number]>>(new Map());
  const reqIdRef = useRef(0);
  const loadedRef = useRef(false);
  // Seller whose popup should open once one of its leaves renders individually.
  const pendingOpenRef = useRef<string | null>(null);

  const onClickRef = useRef(onDealerClick);
  onClickRef.current = onDealerClick;
  const selectedRef = useRef<string | null | undefined>(selectedDealerId);
  selectedRef.current = selectedDealerId;

  // ----- One marker dot, shared by the viewport render and the spiderfy layer -----
  const makeLeafCircle = useCallback((f: LeafFeature, lat: number, lng: number): L.CircleMarker => {
    const dominant: PriorityKey = priorityKeyFromIndex(f.pri);
    const fill = PRIORITY_COLOR[dominant].pin;
    const isSelected = f.sellerId === selectedRef.current;
    const circle = L.circleMarker([lat, lng], {
      renderer: rendererRef.current ?? undefined,
      radius: isSelected ? 9 : 6,
      fillColor: fill,
      fillOpacity: 1,
      color: isSelected ? '#111827' : '#ffffff',
      weight: isSelected ? 4 : 1.5,
    });
    const point = pointByIdRef.current.get(f.id);
    if (point) {
      circle.bindPopup(() => buildPopupHtml(point), {
        maxWidth: 280,
        // Hover-driven card: don't pan the map when it opens (jarring while
        // moving the cursor over points) and skip the close button since
        // mouseout handles dismissal.
        autoPan: false,
        closeButton: false,
      });
    }
    // Open the basic-detail card on hover instead of requiring a click.
    circle.on('mouseover', () => circle.openPopup());
    circle.on('mouseout', () => circle.closePopup());
    circle.on('click', (ev) => {
      L.DomEvent.stopPropagation(ev);
      onClickRef.current?.(f.sellerId);
    });
    return circle;
  }, []);

  const clearSpider = useCallback(() => {
    spiderLayerRef.current?.clearLayers();
    spiderClusterRef.current = null;
  }, []);

  /**
   * Fan a cluster's markers out around its centre, each on a leg back to the
   * shared plot. Vehicles in one ZIP collapse to a single centroid, so there is
   * no geographic spread to zoom into — without this they only separate at max
   * zoom, if at all.
   */
  const spiderfy = useCallback((clusterId: number, lat: number, lng: number, leaves: LeafFeature[]) => {
    const layer = spiderLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    const centre = L.latLng(lat, lng);
    const positions = spiderfyPositions(map, centre, leaves.length);
    leaves.forEach((leaf, i) => {
      const pos = positions[i];
      layer.addLayer(L.polyline([centre, pos], {
        color: '#94a3b8', weight: 1.5, opacity: 0.7, interactive: false,
      }));
      layer.addLayer(makeLeafCircle(leaf, pos.lat, pos.lng));
    });
    spiderClusterRef.current = clusterId;
  }, [map, makeLeafCircle]);

  // ----- Render the worker's viewport features -----
  const render = useCallback((features: OutFeature[]) => {
    const clusterLayer = clusterLayerRef.current;
    const pointLayer = pointLayerRef.current;
    const renderer = rendererRef.current;
    if (!clusterLayer || !pointLayer || !renderer) return;

    clusterLayer.clearLayers();
    pointLayer.clearLayers();
    circleByIdRef.current.clear();

    const leaves = features.filter((f): f is Extract<OutFeature, { cluster: false }> => !f.cluster);
    const plot = fanOutLeaves(leaves.map((l) => ({ id: l.id, lat: l.lat, lng: l.lng })));

    for (const f of features) {
      if (f.cluster) {
        const dominant: PriorityKey = priorityKeyFromIndex(f.minPri);
        const bg = PRIORITY_COLOR[dominant].pin;
        const size = f.vehicles >= 50 ? 'large' : f.vehicles >= 10 ? 'medium' : 'small';
        const icon = L.divIcon({
          html: `<div class="bv-cluster bv-cluster-${size}" style="background:${bg}"><span>${f.vehicles}</span></div>`,
          className: 'bv-cluster-wrapper',
          iconSize: L.point(36, 36),
        });
        const marker = L.marker([f.lat, f.lng], { icon });
        marker.on('click', (ev) => {
          L.DomEvent.stopPropagation(ev);
          if (spiderClusterRef.current === f.id) { clearSpider(); return; }
          clearSpider();
          // The worker decides: fan out its markers, or tell us the zoom the
          // cluster actually splits at (never just "current + 2").
          workerRef.current?.postMessage({
            type: 'expand', clusterId: f.id, lat: f.lat, lng: f.lng, limit: SPIDERFY_MAX,
          });
        });
        clusterLayer.addLayer(marker);
      } else {
        const [lat, lng] = plot.get(f.id) ?? [f.lat, f.lng];
        const circle = makeLeafCircle(f, lat, lng);
        pointLayer.addLayer(circle);
        circleByIdRef.current.set(f.id, circle);

        // The selection effect may have asked us to open this dealer's popup
        // once one of its vehicles became an individually-rendered leaf (e.g.
        // after zooming in far enough to break its cluster).
        if (pendingOpenRef.current && pendingOpenRef.current === f.sellerId) {
          circle.openPopup();
          pendingOpenRef.current = null;
        }
      }
    }
  }, [makeLeafCircle, clearSpider]);

  // ----- Query the worker for the current viewport -----
  const query = useCallback(() => {
    if (!loadedRef.current || !workerRef.current) return;
    const b = map.getBounds();
    const bbox: [number, number, number, number] = [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()];
    const id = ++reqIdRef.current;
    workerRef.current.postMessage({ type: 'query', id, bbox, zoom: Math.round(map.getZoom()) });
  }, [map]);

  // ----- One-time setup: renderer, layers, worker, map handlers -----
  useEffect(() => {
    const renderer = L.canvas({ padding: 0.5 });
    rendererRef.current = renderer;
    const clusterLayer = L.layerGroup().addTo(map);
    const pointLayer = L.layerGroup().addTo(map);
    const spiderLayer = L.layerGroup().addTo(map);
    const circleById = circleByIdRef.current;
    clusterLayerRef.current = clusterLayer;
    pointLayerRef.current = pointLayer;
    spiderLayerRef.current = spiderLayer;

    const worker = new Worker(new URL('./clusterWorker.ts', import.meta.url), { type: 'module' });
    workerRef.current = worker;
    worker.onmessage = (event: MessageEvent) => {
      const msg = event.data;
      if (msg.type === 'loaded') {
        loadedRef.current = true;
        query();
      } else if (msg.type === 'result') {
        if (msg.id !== reqIdRef.current) return; // drop stale viewport responses
        render(msg.features as OutFeature[]);
      } else if (msg.type === 'expanded') {
        if (msg.leaves.length > 0) {
          spiderfy(msg.clusterId, msg.lat, msg.lng, msg.leaves as LeafFeature[]);
        } else {
          map.setView([msg.lat, msg.lng], Math.min(msg.expansionZoom, map.getMaxZoom()));
        }
      }
    };

    const handleBackgroundClick = () => {
      clearSpider();
      onClickRef.current?.(null);
    };
    map.on('moveend', query);
    // A fan-out is anchored to one screen position, so it can't survive a move.
    map.on('movestart', clearSpider);
    map.on('click', handleBackgroundClick);

    return () => {
      map.off('moveend', query);
      map.off('movestart', clearSpider);
      map.off('click', handleBackgroundClick);
      worker.terminate();
      workerRef.current = null;
      map.removeLayer(clusterLayer);
      map.removeLayer(pointLayer);
      map.removeLayer(spiderLayer);
      clusterLayerRef.current = null;
      pointLayerRef.current = null;
      spiderLayerRef.current = null;
      circleById.clear();
    };
  }, [map, query, render, spiderfy, clearSpider]);

  // ----- (Re)load the worker index whenever the point set changes -----
  useEffect(() => {
    const byId = new Map<string, BrokerVehiclePoint>();
    const sellerCenter = new Map<string, [number, number]>();
    const payload: { id: string; sellerId: string; lng: number; lat: number; pri: number; count: number }[] = [];
    points.forEach((p, i) => {
      if (p.latitude == null || p.longitude == null) return;
      const id = String(i);
      byId.set(id, p);
      if (!sellerCenter.has(p.seller_id)) {
        sellerCenter.set(p.seller_id, [p.latitude, p.longitude]);
      }
      payload.push({
        id,
        sellerId: p.seller_id,
        lng: p.longitude,
        lat: p.latitude,
        pri: p.priority_bucket,
        count: p.count,
      });
    });
    pointByIdRef.current = byId;
    sellerCenterRef.current = sellerCenter;
    loadedRef.current = false;
    workerRef.current?.postMessage({ type: 'load', points: payload });
  }, [points]);

  // ----- Selection: highlight, pan to, and open the selected dealer -----
  useEffect(() => {
    pendingOpenRef.current = selectedDealerId ?? null;
    if (selectedDealerId) {
      const center = sellerCenterRef.current.get(selectedDealerId);
      if (center) {
        const zoom = map.getZoom();
        // Zoom in far enough to break the dealer's vehicles out of their cluster
        // so an individual pin (and its popup) becomes visible.
        map.setView(center, zoom < 14 ? 14 : zoom);
      }
    }
    // Re-render so the highlight (and any popup) updates even when the view
    // didn't move. A stale-id guard drops the redundant query if a moveend
    // from the setView above also fires.
    query();
  }, [selectedDealerId, map, query]);

  return null;
};

/**
 * Calls `map.invalidateSize()` whenever the map's container element resizes
 * (e.g. when the user drags the carousel/map splitter). Without this,
 * Leaflet keeps the previous tile size and renders blank space.
 */
const MapResizeObserver: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();
    if (!container || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      // rAF avoids layout thrash mid-drag
      window.requestAnimationFrame(() => map.invalidateSize());
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [map]);
  return null;
};

/**
 * Tracks the effective theme (light/dark) following the data-theme attribute
 * and the system color-scheme preference when no explicit theme is set.
 */

// ---- §10-E seller-focus overlay -------------------------------------------
const money = (n?: number | null) => (n == null ? '—' : `$${Math.round(n).toLocaleString()}`);

/** A candidate-buyer pin — a blue diamond, distinct from the urgency vehicle dots. */
const buyerDiamondIcon = (on: boolean): L.DivIcon =>
  L.divIcon({
    className: 'bv-buyer-icon-wrap',
    html: `<div class="bv-buyer-icon${on ? ' bv-buyer-icon--on' : ''}"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });

/** The seller's own location — a bold, high-contrast hub so it never gets lost
 * under the unit + buyer pins. Carries the targeted-unit count. */
const sellerHubIcon = (n: number): L.DivIcon =>
  L.divIcon({
    className: 'bv-seller-hub-wrap',
    html: `<div class="bv-seller-hub"><span class="bv-seller-hub-glyph">◎</span><span class="bv-seller-hub-count">${n}</span></div>`,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });

/** A buyer-focus hub — the focused buyer's own location, a bold blue diamond. */
const buyerHubIcon = (n: number): L.DivIcon =>
  L.divIcon({
    className: 'bv-seller-hub-wrap',
    html: `<div class="bv-buyer-hub"><span class="bv-buyer-hub-glyph">◆</span><span class="bv-seller-hub-count">${n}</span></div>`,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  });

/**
 * The slice-focus layer (children of the same MapContainer): a focused card's
 * targeted units (urgency dots, fanned so co-located units don't stack) + the
 * candidate buyers for them (blue diamonds) + buyer↔vehicle proximity edges on
 * hover, plus a bold anchor hub for the focused entity (seller/buyer location).
 * Fetches its own bounded data set. Slice-aware:
 *   - seller/cluster: buyers = each VIN's top-5; seller anchor = units centroid.
 *   - buyer: units = the buyer's matches; the focused buyer anchors the map.
 */
const SliceFocusLayer: React.FC<{
  slice: 'seller' | 'buyer' | 'cluster';
  focusKey: string;
  params?: ClientViewParams & BrokerChipFilterParams & { match_types?: string };
  onVinClick?: (vin: string) => void;
  onInfo?: (info: { vehicles: number; buyers: number; warming: boolean; loading: boolean; error: string | null }) => void;
}> = ({ slice, focusKey, params, onVinClick, onInfo }) => {
  const map = useMap();
  const [vehicles, setVehicles] = useState<BrokerSliceMapVehicle[]>([]);
  const [buyers, setBuyers] = useState<BrokerSliceMapBuyer[]>([]);
  const [hoverVin, setHoverVin] = useState<string | null>(null);
  const [hoverBuyer, setHoverBuyer] = useState<string | null>(null);
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);
  const infoRef = useRef(onInfo);
  infoRef.current = onInfo;

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    infoRef.current?.({ vehicles: 0, buyers: 0, warming: false, loading: true, error: null });
    (async () => {
      try {
        const res = await siftApiClient.getBrokerViewSliceMap(slice, focusKey, params, controller.signal);
        if (cancelled) return;
        setVehicles(res.vehicles);
        setBuyers(res.buyers);
        infoRef.current?.({ vehicles: res.vehicles.length, buyers: res.buyers.length, warming: res.matches_warming, loading: false, error: null });
      } catch (err: any) {
        if (cancelled || controller.signal.aborted || err?.code === 'ERR_CANCELED') return;
        setVehicles([]); setBuyers([]);
        infoRef.current?.({ vehicles: 0, buyers: 0, warming: false, loading: false, error: err?.response?.data?.detail || 'Failed to load the map' });
      }
    })();
    return () => { cancelled = true; controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slice, focusKey, paramsKey]);

  const geoVehicles = useMemo(() => vehicles.filter((v) => v.latitude != null && v.longitude != null), [vehicles]);
  const buyerByUid = useMemo(() => new Map(buyers.map((b) => [b.buyer_uid, b])), [buyers]);
  const vehByVin = useMemo(() => new Map(geoVehicles.map((v) => [v.vin, v])), [geoVehicles]);

  // Fan co-located unit pins into a small ring so stacked inventory reads as many
  // units (and stays individually clickable) around the anchor.
  const unitPlot = useMemo(
    () => fanOutLeaves(geoVehicles.map((v) => ({ id: v.vin, lat: v.latitude, lng: v.longitude }))),
    [geoVehicles],
  );
  // The focused buyer (buyer slice only) — it anchors the map and is drawn as a
  // hub rather than a small diamond.
  const focusedBuyer = useMemo(
    () => (slice === 'buyer' ? buyers.find((b) => b.buyer_uid === focusKey && b.latitude != null) ?? null : null),
    [slice, buyers, focusKey],
  );
  // Anchor hub position: seller = units centroid (all at the seller ZIP offsite);
  // buyer = the focused buyer's location; cluster = none (units span the map).
  const anchor = useMemo<[number, number] | null>(() => {
    if (slice === 'buyer') return focusedBuyer ? [focusedBuyer.latitude, focusedBuyer.longitude] : null;
    if (slice === 'cluster') return null;
    if (!geoVehicles.length) return null;
    const lat = geoVehicles.reduce((s, v) => s + v.latitude, 0) / geoVehicles.length;
    const lng = geoVehicles.reduce((s, v) => s + v.longitude, 0) / geoVehicles.length;
    return [lat, lng];
  }, [slice, focusedBuyer, geoVehicles]);

  // Fit the map to the units + buyers whenever the data set changes.
  const bounds = useMemo<[number, number][]>(
    () => [...geoVehicles.map((v) => [v.latitude, v.longitude] as [number, number]),
      ...buyers.filter((b) => b.latitude != null).map((b) => [b.latitude, b.longitude] as [number, number])],
    [geoVehicles, buyers],
  );
  const boundsKey = bounds.length ? `${slice}:${focusKey}:${bounds.length}` : `empty:${slice}:${focusKey}`;
  const prevBounds = useRef('');
  useEffect(() => {
    if (boundsKey === prevBounds.current) return;
    prevBounds.current = boundsKey;
    if (bounds.length > 0) map.fitBounds(L.latLngBounds(bounds), { padding: [56, 56], maxZoom: 11 });
  }, [boundsKey, bounds, map]);

  const edges = useMemo<[[number, number], [number, number]][]>(() => {
    const out: [[number, number], [number, number]][] = [];
    if (hoverVin) {
      const v = vehByVin.get(hoverVin);
      const from = v ? unitPlot.get(v.vin) ?? [v.latitude, v.longitude] : null;
      if (v && from) for (const uid of v.buyer_uids) {
        const b = buyerByUid.get(uid);
        if (b) out.push([from, [b.latitude, b.longitude]]);
      }
    } else if (hoverBuyer) {
      const b = buyerByUid.get(hoverBuyer);
      if (b) for (const vin of b.vins) {
        const v = vehByVin.get(vin);
        const to = v ? unitPlot.get(v.vin) ?? [v.latitude, v.longitude] : null;
        if (to) out.push([[b.latitude, b.longitude], to]);
      }
    }
    return out;
  }, [hoverVin, hoverBuyer, vehByVin, buyerByUid, unitPlot]);

  const activeBuyerUids = hoverVin ? new Set(vehByVin.get(hoverVin)?.buyer_uids ?? []) : null;
  const activeVins = hoverBuyer ? new Set(buyerByUid.get(hoverBuyer)?.vins ?? []) : null;

  return (
    <>
      {edges.map((e, i) => (
        <Polyline key={i} positions={e} pathOptions={{ color: '#2563eb', weight: 1.5, opacity: 0.55, dashArray: '4 4' }} />
      ))}

      {/* Buyer diamonds (drawn under the units). For a buyer focus the focused
          buyer is drawn as the anchor hub below, not a small diamond. */}
      {buyers.filter((b) => b.latitude != null && !(slice === 'buyer' && b.buyer_uid === focusKey)).map((b) => {
        const on = hoverBuyer === b.buyer_uid || (activeBuyerUids?.has(b.buyer_uid) ?? false);
        return (
          <Marker
            key={`b-${b.buyer_uid}`}
            position={[b.latitude, b.longitude]}
            icon={buyerDiamondIcon(on)}
            zIndexOffset={on ? 900 : 0}
            eventHandlers={{
              mouseover: () => setHoverBuyer(b.buyer_uid),
              mouseout: () => setHoverBuyer((cur) => (cur === b.buyer_uid ? null : cur)),
            }}
          >
            <Tooltip direction="top" offset={[0, -8]}>
              <div className="bv-map-tip">
                <div className="bv-map-tip-name">{b.buyer_name || `Buyer ${b.buyer_uid}`}</div>
                <div className="bv-map-tip-sub">{[b.state, b.zip].filter(Boolean).join(' · ')}</div>
                <div className="bv-map-tip-meta">Best: {MATCH_TIER_LABEL[b.best_tier] ?? '—'} · fits {b.matching_vins} unit{b.matching_vins === 1 ? '' : 's'}</div>
              </div>
            </Tooltip>
          </Marker>
        );
      })}

      {/* Unit dots (urgency-colored, fanned, drawn above the buyers). */}
      {geoVehicles.map((v) => {
        const key = priorityKeyFromIndex(v.severity_tier);
        const on = hoverVin === v.vin || (activeVins?.has(v.vin) ?? false);
        const [lat, lng] = unitPlot.get(v.vin) ?? [v.latitude, v.longitude];
        return (
          <CircleMarker
            key={`v-${v.vin}`}
            center={[lat, lng]}
            radius={on ? 9 : 6.5}
            pathOptions={{ color: on ? '#111827' : '#1f2937', weight: on ? 3 : 1.5, fillColor: PRIORITY_COLOR[key].pin, fillOpacity: 1 }}
            eventHandlers={{
              mouseover: () => setHoverVin(v.vin),
              mouseout: () => setHoverVin((cur) => (cur === v.vin ? null : cur)),
              click: () => onVinClick?.(v.vin),
            }}
          >
            <Tooltip direction="top" offset={[0, -6]}>
              <div className="bv-map-tip">
                <div className="bv-map-tip-name">{v.label}</div>
                <div className="bv-map-tip-sub">{money(v.price)} · {PRIORITY_LABEL[key]}</div>
                <div className="bv-map-tip-meta">{v.buyer_uids.length} matched buyer{v.buyer_uids.length === 1 ? '' : 's'}{onVinClick ? ' · click to open' : ''}</div>
              </div>
            </Tooltip>
          </CircleMarker>
        );
      })}

      {/* Anchor hub — the focused entity's own location, bold + on top so it
          never gets lost. Seller = units centroid (non-interactive); buyer = the
          focused buyer (hover → edges to all its units); cluster = none. */}
      {anchor && slice !== 'cluster' && (
        slice === 'buyer' ? (
          <Marker
            position={anchor}
            icon={buyerHubIcon(focusedBuyer?.matching_vins ?? geoVehicles.length)}
            zIndexOffset={1200}
            eventHandlers={{
              mouseover: () => setHoverBuyer(focusKey),
              mouseout: () => setHoverBuyer((cur) => (cur === focusKey ? null : cur)),
            }}
          >
            <Tooltip direction="top" offset={[0, -14]}>
              <div className="bv-map-tip">
                <div className="bv-map-tip-name">{focusedBuyer?.buyer_name || `Buyer ${focusKey}`}</div>
                <div className="bv-map-tip-sub">{[focusedBuyer?.state, focusedBuyer?.zip].filter(Boolean).join(' · ')}</div>
                <div className="bv-map-tip-meta">fits {focusedBuyer?.matching_vins ?? geoVehicles.length} board unit{(focusedBuyer?.matching_vins ?? geoVehicles.length) === 1 ? '' : 's'}</div>
              </div>
            </Tooltip>
          </Marker>
        ) : (
          <Marker position={anchor} icon={sellerHubIcon(geoVehicles.length)} zIndexOffset={1200} interactive={false} />
        )
      )}
    </>
  );
};

export const BrokerViewMap: React.FC<BrokerViewMapProps> = React.memo(({
  points,
  loading,
  selectedDealerId,
  onDealerClick,
  focus,
  sellerMapParams,
  onFocusVinClick,
  onClearFocus,
}) => {
  const stablePoints = useMemo(() => points, [points]);
  const focused = focus != null;
  // Seller-focus fetch status (surfaced as a small overlay banner).
  const [focusInfo, setFocusInfo] = useState<{ vehicles: number; buyers: number; warming: boolean; loading: boolean; error: string | null }>(
    { vehicles: 0, buyers: 0, warming: false, loading: false, error: null },
  );

  // Legend visibility — persisted so each user's show/hide preference sticks.
  const [legendHidden, setLegendHidden] = useState<boolean>(
    () => localStorage.getItem('bv-map-legend-hidden') === 'true',
  );
  const toggleLegend = useCallback(() => {
    setLegendHidden((prev) => {
      const next = !prev;
      localStorage.setItem('bv-map-legend-hidden', String(next));
      return next;
    });
  }, []);

  return (
    <div className="bv-map-wrapper">
      {loading && !focused && (
        <div className="bv-map-loading">
          <span>Loading vehicles...</span>
        </div>
      )}
      {focused && (
        <div className="bv-map-focusbar">
          <span className="bv-map-focusbar-label">
            🎯 {focus!.name || focus!.key}
            <span className="bv-map-focusbar-sub">
              {focusInfo.loading ? ' · loading…' : ` · ${focusInfo.vehicles} unit${focusInfo.vehicles === 1 ? '' : 's'} · ${focusInfo.buyers} buyer${focusInfo.buyers === 1 ? '' : 's'}`}
            </span>
          </span>
          {onClearFocus && (
            <button type="button" className="bv-map-focusbar-clear" onClick={onClearFocus} title="Back to the whole board">
              ✕ Show all
            </button>
          )}
        </div>
      )}
      {focused && focusInfo.error && <div className="bv-map-focus-error">{focusInfo.error}</div>}
      {focused && focusInfo.warming && !focusInfo.loading && (
        <div className="bv-map-focus-warming">Buyer matches are still computing — the buyer layer will fill in shortly.</div>
      )}
      <MapContainer
        center={[39.8283, -98.5795]}
        zoom={4}
        className="bv-map"
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        <MapResizeObserver />

        {focused ? (
          <SliceFocusLayer
            slice={focus!.kind}
            focusKey={focus!.key}
            params={sellerMapParams}
            onVinClick={onFocusVinClick}
            onInfo={setFocusInfo}
          />
        ) : (
          <>
            <MapBoundsUpdater points={stablePoints} />
            <SuperclusterCanvasLayer
              points={stablePoints}
              selectedDealerId={selectedDealerId}
              onDealerClick={onDealerClick}
            />
          </>
        )}
      </MapContainer>

      {/* Legend — collapsible; gains the buyer + seller keys in focus mode. */}
      <div className={`bv-map-legend${legendHidden ? ' bv-map-legend-hidden' : ''}`}>
        <button
          type="button"
          className="bv-legend-toggle"
          onClick={toggleLegend}
          title={legendHidden ? 'Show legend' : 'Hide legend'}
          aria-label={legendHidden ? 'Show legend' : 'Hide legend'}
          aria-expanded={!legendHidden}
        >
          {legendHidden ? 'Legend' : '✕'}
        </button>
        {!legendHidden && (
          <>
            <div className="bv-legend-section">
              <div className="bv-legend-title">{focused ? 'Units (urgency)' : 'Priority'}</div>
              {PRIORITY_ORDER.map((k) => (
                <div key={k} className="bv-legend-item">
                  <span className="bv-legend-dot" style={{ background: PRIORITY_COLOR[k].pin }}></span>
                  {PRIORITY_LABEL[k]}
                </div>
              ))}
            </div>
            {focused && (
              <div className="bv-legend-section">
                <div className="bv-legend-title">Map</div>
                {focus!.kind !== 'buyer' && (
                  <div className="bv-legend-item"><span className="bv-legend-diamond"></span>Candidate buyers</div>
                )}
                {focus!.kind === 'seller' && (
                  <div className="bv-legend-item"><span className="bv-legend-hub">◎</span>Seller location</div>
                )}
                {focus!.kind === 'buyer' && (
                  <div className="bv-legend-item"><span className="bv-legend-hub" style={{ color: '#1d4ed8' }}>◆</span>Focused buyer</div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});
